import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BookOpen, User, MapPin, Check, X, Clock, Star, Search, Filter, ArrowUpDown, Loader2, AlertTriangle } from 'lucide-react';
import RatingModal from '../components/RatingModal';
import RequestsTable from '../components/RequestsTable';
import Pagination from '../components/Pagination';
import { usePendingRequests } from '../hooks/usePendingRequests';
import { isOverdue } from '../utils/dateUtils';

interface BookRequest {
  id: string;
  book_id: string;
  title: string;
  author: string;
  cover_image: string;
  status: 'pending' | 'accepted' | 'rejected' | 'cancelled' | 'completed';
  created_at: string;
  owner_name?: string;
  owner_city?: string;
  requester_name?: string;
  requester_city?: string;
  // Requester stats for received requests
  requester_average_rating?: number;
  requester_total_ratings?: number;
  requester_trust_score?: number;
  can_rate?: boolean | number;
  // Rental information
  rental_start_date?: string;
  rental_end_date?: string;
}

const Requests: React.FC = () => {
  const [myRequests, setMyRequests] = useState<BookRequest[]>([]);
  const [receivedRequests, setReceivedRequests] = useState<BookRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'sent' | 'received'>('sent');
  const { refetch: refetchPendingCount } = usePendingRequests();
  
  // États pour la pagination et le tri
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [ratingModal, setRatingModal] = useState<{
    isOpen: boolean;
    bookRequestId: string;
    bookTitle: string;
    userName: string;
  }>({
    isOpen: false,
    bookRequestId: '',
    bookTitle: '',
    userName: ''
  });
  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    requestId: string;
    bookTitle: string;
  }>({
    isOpen: false,
    requestId: '',
    bookTitle: ''
  });
  const [loadingActions, setLoadingActions] = useState<Record<string, boolean>>({});

  // Filtering states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    
    try {
      const [myRequestsResponse, receivedRequestsResponse] = await Promise.all([
        axios.get('/api/users/my-requests'),
        axios.get('/api/users/received-requests')
      ]);
      
      // Normaliser can_rate en booléen
      const toBool = (v: unknown) => v === true || v === 1 || v === '1';
      
      setMyRequests(myRequestsResponse.data.map((r: any) => ({ 
        ...r, 
        can_rate: toBool(r.can_rate) 
      })));
      setReceivedRequests(receivedRequestsResponse.data.map((r: any) => ({ 
        ...r, 
        can_rate: toBool(r.can_rate) 
      })));
    } catch (error: any) {
      setError('Erreur lors du chargement des demandes');
      console.error('Fetch requests error:', error);
    } finally {
      setLoading(false);
    }
  };

  const respondToRequest = async (requestId: string, status: 'accepted' | 'rejected') => {
    const actionKey = `respond-${requestId}`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await axios.patch(`/api/users/request/${requestId}`, { status });
      fetchRequests(); // Refresh the list
      refetchPendingCount(); // Refresh the pending count in navbar
    } catch (error: any) {
      // Handle timeout specifically
      if (error.message === 'Request timeout') {
        setError('La réponse a pris trop de temps. Vérifiez vos demandes pour voir si elle a été traitée.');
      } else {
        setError(error.response?.data?.error || 'Erreur lors de la réponse à la demande');
      }
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const markAsReturned = async (bookId: string) => {
    const actionKey = `return-${bookId}`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await axios.patch(`/api/users/return-book/${bookId}`);
      fetchRequests(); // Refresh the list
    } catch (error: any) {
      // Handle timeout specifically
      if (error.message === 'Request timeout') {
        setError('Le marquage du retour a pris trop de temps. Vérifiez vos demandes pour voir si elle a été traitée.');
      } else {
        setError(error.response?.data?.error || 'Erreur lors du marquage du retour');
      }
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const openCancelModal = (requestId: string, bookTitle: string) => {
    setCancelModal({
      isOpen: true,
      requestId,
      bookTitle
    });
  };

  const confirmCancelRequest = async () => {
    const actionKey = `cancel-${cancelModal.requestId}`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));
    
    try {
      await axios.patch(`/api/users/cancel-request/${cancelModal.requestId}`);
      setCancelModal({ isOpen: false, requestId: '', bookTitle: '' });
      fetchRequests(); // Refresh the list
    } catch (error: any) {
      // Handle timeout specifically
      if (error.message === 'Request timeout') {
        setError('L\'annulation a pris trop de temps. Vérifiez vos demandes pour voir si elle a été traitée.');
      } else {
        setError(error.response?.data?.error || 'Erreur lors de l\'annulation de la demande');
      }
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  // Fonction pour formater le nom (prénom + initiale du nom)
  const formatOwnerName = (fullName: string) => {
    if (!fullName) return '';
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      return nameParts[0]; // Si un seul mot, on l'affiche tel quel
    }
    const firstName = nameParts[0];
    const lastNameInitial = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return `${firstName} ${lastNameInitial}.`;
  };

  const openRatingModal = (request: BookRequest) => {
    const userName = activeTab === 'sent' ? request.owner_name : request.requester_name;
    setRatingModal({
      isOpen: true,
      bookRequestId: request.id,
      bookTitle: request.title,
      userName: formatOwnerName(userName || '')
    });
  };

  // Filtering and sorting functions
  const filterAndSortRequests = (requests: BookRequest[]) => {
    let filtered = requests;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(request => 
        request.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (activeTab === 'sent' ? request.owner_name : request.requester_name)?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === statusFilter);
    }

    // Tri avec la nouvelle logique
    if (sortColumn) {
      filtered.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        switch (sortColumn) {
          case 'book':
            aValue = a.title.toLowerCase();
            bValue = b.title.toLowerCase();
            break;
          case 'user':
            aValue = (activeTab === 'sent' ? a.owner_name : a.requester_name)?.toLowerCase() || '';
            bValue = (activeTab === 'sent' ? b.owner_name : b.requester_name)?.toLowerCase() || '';
            break;
          case 'status':
            aValue = a.status;
            bValue = b.status;
            break;
          case 'date':
            aValue = new Date(a.created_at);
            bValue = new Date(b.created_at);
            break;
          case 'return':
            aValue = a.rental_end_date ? new Date(a.rental_end_date) : new Date(0);
            bValue = b.rental_end_date ? new Date(b.rental_end_date) : new Date(0);
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const getFilteredRequests = () => {
    const requests = activeTab === 'sent' ? myRequests : receivedRequests;
    return filterAndSortRequests(requests);
  };

  const getPaginatedRequests = () => {
    const filteredRequests = getFilteredRequests();
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedRequests = filteredRequests.slice(startIndex, endIndex);
    
    return {
      requests: paginatedRequests,
      totalPages,
      totalItems: filteredRequests.length
    };
  };

  const handleRatingSubmitted = () => {
    fetchRequests(); // Refresh to update can_rate status
  };

  // Fonction de tri
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Retour à la première page lors du tri
  };

  // Fonction de changement de page
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Fonction de changement du nombre d'éléments par page
  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1); // Retour à la première page
    // Sauvegarder le choix dans localStorage
    localStorage.setItem('requestsItemsPerPage', items.toString());
  };

  // Charger le nombre d'éléments par page depuis localStorage
  useEffect(() => {
    const savedItemsPerPage = localStorage.getItem('requestsItemsPerPage');
    if (savedItemsPerPage) {
      setItemsPerPage(Number(savedItemsPerPage));
    }
  }, []);

  const getStatusBadge = (status: string, request?: BookRequest) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'En attente' },
      accepted: { color: 'bg-green-100 text-green-800', icon: Check, text: 'Acceptée' },
      rejected: { color: 'bg-red-100 text-red-800', icon: X, text: 'Refusée' },
      cancelled: { color: 'bg-gray-100 text-gray-800', icon: X, text: 'Annulée' },
      completed: { color: 'bg-forest-100 text-forest-800', icon: Check, text: 'Terminée' }
    };

    const config = statusConfig[status as keyof typeof statusConfig];
    const Icon = config.icon;

    return (
      <div className="flex items-center space-x-2">
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
          <Icon className="h-3 w-3 mr-1" />
          {config.text}
        </span>
        
        {/* Badge "En retard" pour les demandes acceptées avec date dépassée */}
        {status === 'accepted' && request?.rental_end_date && isOverdue(request.rental_end_date) && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
            <AlertTriangle className="h-3 w-3 mr-1" />
            En retard
          </span>
        )}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-stone-200 border-t-forest-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="font-serif text-3xl text-ink">Demandes d'emprunt</h1>
        <p className="text-gray-600 mt-2">
          Gérez vos demandes d'emprunt et les demandes reçues
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-4 sm:space-x-8 overflow-x-auto">
          <button
            onClick={() => setActiveTab('sent')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'sent'
                ? 'border-forest-600 text-forest-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Mes demandes ({myRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('received')}
            className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap ${
              activeTab === 'received'
                ? 'border-forest-600 text-forest-700'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Demandes reçues ({receivedRequests.length})
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par titre, auteur ou utilisateur..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="flex-shrink-0">
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-forest-600 focus:border-transparent appearance-none bg-white"
              >
                <option value="all">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="accepted">Acceptées</option>
                <option value="rejected">Refusées</option>
                <option value="cancelled">Annulées</option>
                <option value="completed">Terminées</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Requests Table */}
      <RequestsTable
        requests={getPaginatedRequests().requests}
        activeTab={activeTab}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onActionComplete={fetchRequests}
        onOpenRatingModal={openRatingModal}
        onOpenCancelModal={(request) => openCancelModal(request.id, request.title)}
      />

      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        totalPages={getPaginatedRequests().totalPages}
        totalItems={getPaginatedRequests().totalItems}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />

      {/* Rating Modal */}
      <RatingModal
        isOpen={ratingModal.isOpen}
        onClose={() => setRatingModal({ ...ratingModal, isOpen: false })}
        bookRequestId={ratingModal.bookRequestId}
        bookTitle={ratingModal.bookTitle}
        userName={ratingModal.userName}
        onRatingSubmitted={handleRatingSubmitted}
      />

      {/* Cancel Confirmation Modal */}
      {cancelModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <X className="h-6 w-6 text-red-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Annuler la demande
                  </h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-500">
                  Êtes-vous sûr de vouloir annuler votre demande pour le livre : <span className="font-medium text-gray-900">« {cancelModal.bookTitle} »</span>
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Le propriétaire sera notifié de cette annulation.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  onClick={() => setCancelModal({ isOpen: false, requestId: '', bookTitle: '' })}
                  className="btn btn-outline text-sm flex-1 sm:flex-none"
                >
                  Garder la demande
                </button>
                <button
                  onClick={confirmCancelRequest}
                  disabled={loadingActions[`cancel-${cancelModal.requestId}`]}
                  className="btn bg-red-600 text-white text-sm flex-1 sm:flex-none hover:bg-red-700 inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingActions[`cancel-${cancelModal.requestId}`] ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Annulation...
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1" />
                      Annuler la demande
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests; 